import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from '../base/api.service';
import { ReferralSpecialty } from './entities/referral-specialty';
import { ReferralFacilityGroup } from './entities/referral-facility-group';
import { ReferralFacility } from './entities/referral-facility';
import { ReferralDoctor } from './entities/referral-doctor';
import { ReferralSpecialtyCollectionResponse } from './entities/referral-specialty-collection-response';
import { ReferralSpecialtyItemResponse } from './entities/referral-specialty-item-response';
import { ReferralFacilityGroupCollectionResponse } from './entities/referral-facility-group-collection-response';
import { ReferralFacilityGroupItemResponse } from './entities/referral-facility-group-item-response';
import { ReferralFacilityCollectionResponse } from './entities/referral-facility-collection-response';
import { ReferralFacilityItemResponse } from './entities/referral-facility-item-response';
import { ReferralDoctorCollectionResponse } from './entities/referral-doctor-collection-response';
import { ReferralDoctorItemResponse } from './entities/referral-doctor-item-response';

@Injectable({
  providedIn: 'root'
})
export class ReferralDirectoryRepositoryService {
  constructor(private api: ApiService) {}

  // Specialties
  getSpecialties(): Observable<ReferralSpecialty[]> {
    return this.api.get<ReferralSpecialtyCollectionResponse>('/referral-directory/specialties')
      .pipe(map(response => response.items));
  }

  getSpecialtyByUid(uid: string): Observable<ReferralSpecialty> {
    return this.api.get<ReferralSpecialtyItemResponse>('/referral-directory/specialties/' + uid)
      .pipe(map(response => response.value));
  }

  getDoctorsBySpecialty(specialtyId: number): Observable<ReferralDoctor[]> {
    return this.api.get<ReferralDoctorCollectionResponse>('/referral-directory/specialties/' + specialtyId + '/doctors')
      .pipe(map(response => response.items));
  }

  // Facility Groups
  getFacilityGroups(): Observable<ReferralFacilityGroup[]> {
    return this.api.get<ReferralFacilityGroupCollectionResponse>('/referral-directory/facility-groups')
      .pipe(map(response => response.items));
  }

  getFacilityGroupByUid(uid: string): Observable<ReferralFacilityGroup> {
    return this.api.get<ReferralFacilityGroupItemResponse>('/referral-directory/facility-groups/' + uid)
      .pipe(map(response => response.value));
  }

  searchFacilityGroups(query: string): Observable<ReferralFacilityGroup[]> {
    return this.api.get<ReferralFacilityGroupCollectionResponse>('/referral-directory/facility-groups/search', { query })
      .pipe(map(response => response.items));
  }

  getFacilitiesByGroup(groupId: number): Observable<ReferralFacility[]> {
    return this.api.get<ReferralFacilityCollectionResponse>('/referral-directory/facility-groups/' + groupId + '/facilities')
      .pipe(map(response => response.items));
  }

  getDoctorsByGroup(groupId: number): Observable<ReferralDoctor[]> {
    return this.api.get<ReferralDoctorCollectionResponse>('/referral-directory/facility-groups/' + groupId + '/doctors')
      .pipe(map(response => response.items));
  }

  // Facilities
  getFacilities(): Observable<ReferralFacility[]> {
    return this.api.get<ReferralFacilityCollectionResponse>('/referral-directory/facilities')
      .pipe(map(response => response.items));
  }

  getFacilityByUid(uid: string): Observable<ReferralFacility> {
    return this.api.get<ReferralFacilityItemResponse>('/referral-directory/facilities/' + uid)
      .pipe(map(response => response.value));
  }

  searchFacilities(query: string): Observable<ReferralFacility[]> {
    return this.api.get<ReferralFacilityCollectionResponse>('/referral-directory/facilities/search', { query })
      .pipe(map(response => response.items));
  }

  getDoctorsByFacility(facilityId: number): Observable<ReferralDoctor[]> {
    return this.api.get<ReferralDoctorCollectionResponse>('/referral-directory/facilities/' + facilityId + '/doctors')
      .pipe(map(response => response.items));
  }

  // Doctors
  getDoctors(): Observable<ReferralDoctor[]> {
    return this.api.get<ReferralDoctorCollectionResponse>('/referral-directory/doctors')
      .pipe(map(response => response.items));
  }

  getDoctorByUid(uid: string): Observable<ReferralDoctor> {
    return this.api.get<ReferralDoctorItemResponse>('/referral-directory/doctors/' + uid)
      .pipe(map(response => response.value));
  }

  getDoctorByPracticeNumber(practiceNumber: string): Observable<ReferralDoctor> {
    return this.api.get<ReferralDoctorItemResponse>('/referral-directory/doctors/by-practice-number/' + practiceNumber)
      .pipe(map(response => response.value));
  }

  searchDoctors(query: string): Observable<ReferralDoctor[]> {
    return this.api.get<ReferralDoctorCollectionResponse>('/referral-directory/doctors/search', { query })
      .pipe(map(response => response.items));
  }
}
